import React, { useState } from 'react';
import { CheckCircle, Calendar, MapPin, ArrowRight, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Projects = () => {
  const { language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const projects = [
    {
      id: 1,
      title: { fr: 'Réfection de Bureaux - Structures Publiques', en: 'Office Renovation - Public Institutions' },
      description: { fr: 'Rénovation complète de bureaux pour des administrations publiques avec mobilier moderne', en: 'Complete office renovation for public administrations with modern furniture' },
      category: { fr: 'Aménagement', en: 'Office Setup' },
      date: '2024-2025',
      location: { fr: 'Dakar, Sénégal', en: 'Dakar, Senegal' },
      image: 'https://images.unsplash.com/photo-1631193816258-28b44b21e78b',
      achievements: [
        { fr: '15 bureaux rénovés', en: '15 offices renovated' },
        { fr: 'Mobilier ergonomique installé', en: 'Ergonomic furniture installed' },
        { fr: 'Délai respecté à 100%', en: '100% on-time delivery' }
      ]
    },
    {
      id: 2,
      title: { fr: 'Ordinateurs et Imprimantes Evolis Primacy 2', en: 'Computers and Evolis Primacy 2 Printers' },
      description: { fr: 'Vente et installation d\'ordinateurs et imprimantes Evolis pour établissements publics et privés', en: 'Sale and installation of computers and Evolis printers for public and private institutions' },
      category: { fr: 'Informatique', en: 'IT Equipment' },
      date: '2024-2025',
      location: { fr: 'National, Sénégal', en: 'Nationwide, Senegal' },
      image: 'https://customer-assets.emergentagent.com/job_aircraft-portal/artifacts/2a2zuir8_EVolis%20Primacy%202.jpg',
      achievements: [
        { fr: '150+ ordinateurs déployés', en: '150+ computers deployed' },
        { fr: '25 imprimantes Evolis Primacy 2', en: '25 Evolis Primacy 2 printers' },
        { fr: 'Formation utilisateurs incluse', en: 'User training included' }
      ]
    },
    {
      id: 3,
      title: { fr: 'Vente de Consommables Informatiques et Matériels', en: 'Sale of IT Consumables and Equipment' },
      description: { fr: 'Fourniture régulière de consommables informatiques et matériels pour entreprises et administrations', en: 'Regular supply of IT consumables and equipment for businesses and administrations' },
      category: { fr: 'Consommables', en: 'Supplies' },
      date: '2023-2025',
      location: { fr: 'Dakar, Sénégal', en: 'Dakar, Senegal' },
      image: 'https://images.unsplash.com/photo-1635861321688-b63d28749a82',
      achievements: [
        { fr: '1000+ cartouches d\'encre fournies', en: '1000+ ink cartridges supplied' },
        { fr: 'Livraison mensuelle garantie', en: 'Guaranteed monthly delivery' },
        { fr: '30+ clients réguliers', en: '30+ regular clients' }
      ]
    },
    {
      id: 4,
      title: { fr: 'Installation de Serres Agricoles', en: 'Agricultural Greenhouse Installation' },
      description: { fr: 'Construction et installation de serres modernes pour projets agricoles', en: 'Construction and installation of modern greenhouses for agricultural projects' },
      category: { fr: 'Agriculture', en: 'Agriculture' },
      date: '2026',
      location: { fr: 'Thiès, Sénégal', en: 'Thiès, Senegal' },
      image: 'https://images.unsplash.com/photo-1524486361537-8ad15938e1a3',
      achievements: [
        { fr: '5 serres de 500m² installées', en: '5 greenhouses of 500m² installed' },
        { fr: 'Système d\'irrigation intégré', en: 'Integrated irrigation system' },
        { fr: 'Augmentation de 300% du rendement', en: '300% yield increase' }
      ]
    },
    {
      id: 5,
      title: { fr: 'Développement de Logiciel ERP', en: 'ERP Software Development' },
      description: { fr: 'Conception et développement de solution ERP personnalisée pour grande entreprise', en: 'Design and development of customized ERP solution for large company' },
      category: { fr: 'Développement', en: 'Development' },
      date: '2023-2025',
      location: { fr: 'Dakar, Sénégal', en: 'Dakar, Senegal' },
      image: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6',
      achievements: [
        { fr: 'ERP complet développé', en: 'Complete ERP developed' },
        { fr: 'Intégration comptabilité & stocks', en: 'Accounting & inventory integration' },
        { fr: '200+ utilisateurs formés', en: '200+ users trained' }
      ]
    },
    {
      id: 6,
      title: { fr: 'Vente de Matériels de Sport', en: 'Sports Equipment Sales' },
      description: { fr: 'Fourniture d\'équipements sportifs pour écoles et clubs', en: 'Supply of sports equipment for schools and clubs' },
      category: { fr: 'Sport', en: 'Sports' },
      date: '2025',
      location: { fr: 'National, Sénégal', en: 'Nationwide, Senegal' },
      image: 'https://images.unsplash.com/photo-1602211844066-d3bb556e983b',
      achievements: [
        { fr: '500+ équipements sportifs livrés', en: '500+ sports equipment delivered' },
        { fr: '20 écoles équipées', en: '20 schools equipped' },
        { fr: 'Installation et formation incluses', en: 'Installation and training included' }
      ]
    },
    {
      id: 7,
      title: { fr: 'Livraison de Tenues Professionnelles', en: 'Professional Uniforms Delivery' },
      description: { fr: 'Fourniture de tenues de travail pour entreprises de tous secteurs', en: 'Supply of work uniforms for companies in all sectors' },
      category: { fr: 'Équipements', en: 'Equipment' },
      date: '2023-2024',
      location: { fr: 'National, Sénégal', en: 'Nationwide, Senegal' },
      image: 'https://images.unsplash.com/photo-1598679253549-c215e0b9ee1e',
      achievements: [
        { fr: '2000+ tenues livrées', en: '2000+ uniforms delivered' },
        { fr: 'Personnalisation logo entreprise', en: 'Company logo customization' },
        { fr: '50+ entreprises servies', en: '50+ companies served' }
      ]
    }
  ];

  const categories = [
    { id: 'all', name: { fr: 'Tous les projets', en: 'All projects' } },
    { id: 'Aménagement', name: { fr: 'Aménagement', en: 'Office Setup' } },
    { id: 'Informatique', name: { fr: 'Informatique', en: 'IT Equipment' } },
    { id: 'Consommables', name: { fr: 'Consommables', en: 'Supplies' } },
    { id: 'Agriculture', name: { fr: 'Agriculture', en: 'Agriculture' } },
    { id: 'Développement', name: { fr: 'Développement', en: 'Development' } },
    { id: 'Sport', name: { fr: 'Sport', en: 'Sports' } },
    { id: 'Équipements', name: { fr: 'Équipements', en: 'Equipment' } }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category[language] === selectedCategory || project.category.en === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-blue-900/85"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Nos Réalisations' : 'Our Projects'}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Découvrez nos projets réussis et notre expertise à travers le Sénégal'
              : 'Discover our successful projects and expertise across Senegal'}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
              <div className="text-gray-600">{language === 'fr' ? 'Projets réalisés' : 'Projects completed'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600">{language === 'fr' ? 'Clients satisfaits' : 'Satisfied clients'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">200M+</div>
              <div className="text-gray-600">{language === 'fr' ? 'FCFA investis' : 'FCFA invested'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-600">{language === 'fr' ? 'Taux de réussite' : 'Success rate'}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-3 mb-4">
            <Filter className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {language === 'fr' ? 'Filtrer par catégorie' : 'Filter by category'}
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                className={selectedCategory === category.id ? 'bg-blue-600 text-white' : ''}
              >
                {category.name[language]}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-gray-600">
              {filteredProjects.length} {language === 'fr' ? 'projet(s) trouvé(s)' : 'project(s) found'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                {/* Project Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title[language]}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {project.category[language]}
                    </span>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {project.title[language]}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {project.description[language]}
                  </p>

                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{project.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="h-4 w-4" />
                      <span className="truncate">{project.location[language]}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <ul className="space-y-2">
                      {project.achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{achievement[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Prêt à démarrer votre projet ?' : 'Ready to start your project?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé'
              : 'Contact us to discuss your needs and get a personalized quote'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Démarrer un projet' : 'Start a project'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Projects;